<template>
  <div v-shortkey="['ctrl', '/']" class="d-flex flex-grow-1 layout-root density-compact" :class="globalTheme ? 'theme-dark' : 'theme-light'" :style="layoutAccentStyle" @shortkey="onKeyup">
    
    <!-- 
    |========================================================================
    | 1. FONDO AMBIENTE GLOBAL
    |========================================================================
    | Crea las manchas de color difuminadas detrás de toda la aplicación 
    | para lograr el auténtico efecto Glassmorphism de Apple.
    -->
    <div class="global-ambient-bg">
      <div class="ambient-blob ambient-blob-1"></div>
      <div class="ambient-blob ambient-blob-2"></div>
      <div class="ambient-blob ambient-blob-3"></div>
    </div>

    <!-- 
    |========================================================================
    | 2. MENÚ DE NAVEGACIÓN LATERAL (DRAWER)
    |========================================================================
    | Panel temporal superpuesto.
    -->
    <v-navigation-drawer
      v-model="drawer" app floating :temporary="!isPinned" hide-overlay
      class="elevation-0 apple-glass-pane border-right sb-drawer"
      :class="globalTheme ? 'apple-glass-drawer-dark' : 'apple-glass-drawer-light'"
      :right="$vuetify.rtl"
    >
      <!-- sb-head: Logo + Pin -->
      <template v-slot:prepend>
        <div class="sb-head">
          <div class="sb-logo">
            <img :src="require('@/assets/images/logo.png')" alt="ihosting" />
          </div>
          <button class="sb-pin" :class="{ active: isPinned }" @click.stop="togglePin" title="Fijar menú lateral">
            <i :class="isPinned ? 'mdi mdi-pin' : 'mdi mdi-pin-outline'" style="font-size: 15px" />
          </button>
        </div>
        <!-- sb-search: abre Cmd+K / Ctrl+K -->
        <!-- <div class="sb-search" @click="openGlobalSearch">
          <i class="mdi mdi-magnify" />
          <span class="text">Buscar páginas...</span>
          <kbd class="kbd">Ctrl K</kbd>
        </div> -->
      </template>

      <!-- Client Context Banner (shown when a client is selected) -->
      <client-context-banner v-if="hasClientContext" />

      <!-- Árbol del Menú de Navegación Principal -->
      <main-menu :menu="verifyPermissions"/>

      <!-- sb-foot -->
      <template v-slot:append>
        <div class="sb-foot">
          <span>v{{ appVersion }} · refresh</span>
          <a
            v-for="(item, index) in navigation.footer" :key="index"
            :href="item.href" :target="item.target"
            class="sb-foot-link"
          >{{ item.key ? $t(item.key) : item.text }}</a>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- 
    |========================================================================
    | 3. ENVOLTORIO DEL CONTENIDO PRINCIPAL
    |========================================================================
    | Contiene la cabecera de cristal fija (sticky) y el espacio de la página.
    -->
    <v-main>
      <!-- Cabecera Fija Personalizada en CSS Puro -->
      <header
        class="custom-glass-header apple-glass-pane border-bottom"
        :class="[
          globalTheme ? 'apple-glass-header-dark' : 'apple-glass-header-light',
          { 'header-shifted': isPinned && drawer }
        ]"
        :style="currentModule ? { borderTop: `2.5px solid ${currentModule.color}` } : { borderTop: 'none' }"
      >
        <div class="header-content">
          <!-- Mobile search -->
          <v-text-field
            v-if="showSearch"
            append-icon="mdi-close" prepend-inner-icon="mdi-magnify"
            placeholder="Buscar" hide-details solo flat autofocus
            @click:append="showSearch = false"
            style="max-width: 400px;"
          ></v-text-field>

          <!-- Standard header -->
          <template v-else>
            <!-- Hamburger -->
            <button class="minimalist-hamburger" :class="{ 'is-active': drawer }" @click.stop="drawer = !drawer" aria-label="Menu">
              <div class="hamburger-inner"></div>
            </button>

            <!-- Module chip -->
            <span v-if="currentModule" class="module-chip" :style="currentModuleStyle">
              <i :class="'mdi ' + currentModule.icon" />
              {{ currentModule.label }}
            </span>

            <!-- Client context chip -->
            <v-chip
              v-if="hasClientContext"
              small close
              color="teal" text-color="white"
              class="ml-2 d-none d-sm-flex"
              style="max-width:160px; font-size:11px; cursor:pointer;"
              @click:close="clearClientContext"
            >
              <v-icon left small>mdi-account-check</v-icon>
              <span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ clientContextName }}</span>
            </v-chip>

            <!-- Breadcrumb — solo desktop -->
            <div v-if="currentBreadcrumb" class="hdr-crumbs d-none d-lg-flex">
              <i class="mdi mdi-chevron-right hdr-crumb-sep" />
              <span :class="currentBreadcrumbExtra ? 'hdr-crumb-mid' : 'hdr-crumb-here'">{{ currentBreadcrumb }}</span>
              <template v-if="currentBreadcrumbExtra">
                <i class="mdi mdi-chevron-right hdr-crumb-sep" />
                <span class="hdr-crumb-here">{{ currentBreadcrumbExtra }}</span>
              </template>
            </div>

            <!-- Spacer -->
            <div class="flex-grow-1" />

            <!-- Global search — pill on sm+, icon-only on mobile -->
            <button class="hdr-search d-none d-sm-flex" :class="globalTheme ? 'hdr-search--dark' : 'hdr-search--light'" @click="openGlobalSearch">
              <i class="mdi mdi-magnify" style="font-size: 15px; flex-shrink: 0;" />
              <span class="hdr-search-text d-none d-md-inline">Buscar páginas, clientes…</span>
              <kbd class="hdr-search-kbd">{{ isMac ? '⌘' : 'Ctrl' }} K</kbd>
            </button>
            <button
              class="hdr-search-icon d-flex d-sm-none"
              :class="globalTheme ? 'hdr-search-icon--dark' : 'hdr-search-icon--light'"
              @click="openGlobalSearch"
              aria-label="Buscar"
              title="Buscar"
            >
              <i class="mdi mdi-magnify" />
            </button>

            <!-- Divider -->
            <div class="hdr-divider d-none d-sm-flex" />

            <!-- Toolbar icons -->
            <ToolbarModeDark />
            <toolbar-bug-report />
            <toolbar-notifications />

            <!-- Divider -->
            <div class="hdr-divider" />

            <!-- User -->
            <toolbar-user />
          </template>
        </div>
      </header>

      <!-- El Contenido Principal se Renderiza Aquí (Vistas Router) -->
      <v-container class="fill-height v-container-fluid responsive-v mt-6" :fluid="!isContentBoxed">
        <v-layout>
          <slot></slot>
        </v-layout>
      </v-container>
    </v-main>

    <!-- 
    |========================================================================
    | 5. NAVEGACIÓN INFERIOR (MÓVIL)
    |========================================================================
    | Barra de accesos directos estilo app visible solo en pantallas pequeñas.
    -->
    <nav 
      class="mobile-bottom-nav d-flex d-sm-none apple-glass-pane border-top"
      :class="globalTheme ? 'boxColorStyle_dark' : 'boxColorStyle_light'"
    >
      <router-link 
        v-for="(item, i) in bottomNavItems" :key="i"
        :to="item.link" 
        class="bottom-nav-item" 
        :exact="item.link === '/'"
        active-class="active"
      >
        <v-icon>{{ item.icon }}</v-icon>
        <span>{{ item.text }}</span>
      </router-link>
    </nav>

    <!-- 
    |========================================================================
    | 6. DIÁLOGOS GLOBALES
    |========================================================================
    | Notificaciones a nivel de sistema como la advertencia de Autenticación 
    | de Dos Factores (2FA).
    -->
    <v-dialog v-model="twoFaNotification.status" persistent max-width="450">
      <v-card>
        <v-card-title class="text-h5">{{ twoFaNotification.title }}</v-card-title>
        <v-card-text>{{ twoFaNotification.subtitle }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text to="/setting2fa" @click="twoFaNotification.status = false">
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Global Search Overlay -->
    <global-search
      :open="globalSearchOpen"
      :is-dark="globalTheme"
      @close="globalSearchOpen = false"
      @open-ticket="onSearchOpenTicket"
      @action="onSearchAction"
    />
  </div>
</template>

<script>
/*
|--------------------------------------------------------------------------
| COMPONENTE DE DISEÑO POR DEFECTO (LAYOUT)
|--------------------------------------------------------------------------
| Maneja el envoltorio principal de la aplicación, el panel de navegación
| lateral, la cabecera fija, y validaciones de estado global 
*/

// --- 1. IMPORTACIONES & CONFIGURACIONES ---
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import config from "../configs";
import { version as appVersion } from "../../package.json";

// Componentes
import MainMenu from "../components/navigation/MainMenu";
import ToolbarUser from "../components/toolbar/ToolbarUser";
import ToolbarNotifications from "../components/toolbar/ToolbarNotifications";
import ToolbarBugReport from "../components/toolbar/ToolbarBugReport";
import ToolbarModeDark from "../components/toolbar/ToolbarModeDark.vue";
import GlobalSearch from "../components/common/GlobalSearch.vue";
import ClientContextBanner from "../components/navigation/ClientContextBanner.vue";

// Ventana (ms) para considerar dos Esc como "doble Esc".
const ESC_DOUBLE_WINDOW_MS = 600;

// --- 2. OPCIONES DEL COMPONENTE VUE ---
export default {
  components: {
    MainMenu, ToolbarUser,
    ToolbarNotifications, ToolbarBugReport, ToolbarModeDark, GlobalSearch,
    ClientContextBanner,
  },
  data() {
    // Al cargar por primera vez, el menú debe estar abierto y fijado por defecto (en escritorio)
    const storedPin = typeof window !== 'undefined' ? localStorage.getItem('isMenuPinned') : null;
    const defaultPinDesktop = typeof window !== 'undefined' ? window.innerWidth >= 960 : true;
    const initialPin = storedPin !== null ? storedPin === 'true' : defaultPinDesktop;

    return {
      drawer: initialPin,
      isPinned: initialPin,
      showSearch: false,
      globalSearchOpen: false,
      navigation: config.navigation,
      appVersion,

      twoFaNotification: {
        status: false,
        title: 'Usar doble autenticación',
        subtitle: 'La activación de la doble autenticación previene el acceso no autorizado a tu cuenta en caso de que alguien obtenga tu contraseña. Si un tercero intenta acceder a tu cuenta, pero no tiene acceso al código de seguridad, no podrán ingresar.'
      }
    };
  },

  computed: {
    ...mapState("app", ["product", "isContentBoxed", "menuTheme", "toolbarTheme", "isToolbarDetached", "globalTheme"]),
    ...mapGetters(["isAuthenticated", "google2fa_enable"]),
    ...mapGetters("clientContext", { hasClientContext: "hasContext", clientContextName: "clientDisplayName", _ctxClient: "selectedClient" }),

    isMac() {
      return typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
    },

    verifyPermissions() {
      const { menu } = this.navigation;

      if (!this.isAuthenticated) {
        return [
          {
            text: menu[0].text,
            items: [],
          },
        ];
      }

      // Solo se exponen en el menú las secciones donde el usuario tiene el
      // permiso real (sin permisos temporales agregados solo para el menú).
      const userPermissions = new Set(
        (this.isAuthenticated.user.permisos || []).map((p) => p.name),
      );

      // Un permiso "modulo.view" también se concede si el usuario tiene el
      // comodín del módulo "modulo.*" (así se siembran los permisos en la API:
      // cada módulo crea tanto "modulo.*" como "modulo.view").
      const tienePermiso = (name) => {
        if (!name) return false;
        if (userPermissions.has(name)) return true;
        const dot = name.lastIndexOf('.');
        return dot > -1 && userPermissions.has(name.slice(0, dot) + '.*');
      };

      // Filtro recursivo NO destructivo: devuelve nodos nuevos en lugar de
      // mutar la config de menú compartida (que es un singleton importado). La
      // versión anterior hacía `item.items = item.items.filter(...)`, por lo que
      // si el menú se evaluaba antes de tener los permisos cargados, el ítem
      // quedaba podado de forma permanente hasta recargar la página completa.
      const filtrar = (item) => {
        // Enlace individual con permiso directo (o vía comodín).
        if (item.name && tienePermiso(item.name)) {
          return item;
        }
        // Grupo: se conserva solo si algún hijo sobrevive al filtro.
        if (item.items) {
          const items = item.items.map(filtrar).filter(Boolean);
          return items.length ? { ...item, items } : null;
        }
        return null;
      };

      try {
        const filteredItems = menu[0].items.map(filtrar).filter(Boolean);

        return [
          {
            text: menu[0].text,
            items: filteredItems,
          },
        ];
      } catch (error) {
        this.clerarAuthenticated();
        console.log(error);
        return [
          {
            text: menu[0].text,
            items: [],
          },
        ];
      }
    },

    currentModule() {
      const path = this.$route ? this.$route.path : '';
      const modules = [
        { test: /^\/(apps\/board|tickets)/,                                    label: 'Tickets',        icon: 'mdi-ticket-confirmation-outline',   color: '#0096c7', bg: 'rgba(0,150,199,0.12)' },
        { test: /^\/apps\/servicios/,                                           label: 'Habilitaciones', icon: 'mdi-check-circle-outline',           color: '#0fa3a3', bg: 'rgba(15,163,163,0.12)' },
        { test: /^\/clientes-administrados/,                                    label: 'Clientes Administrados', icon: 'mdi-account-supervisor-outline', color: '#0096c7', bg: 'rgba(0,150,199,0.12)' },
        { test: /^\/(notas-cobros|notas-cobro|transferencia-electronica)/,      label: 'Notas de cobro', icon: 'mdi-note-text-outline',             color: '#c47900', bg: 'rgba(196,121,0,0.12)' },
        { test: /^\/webpay/,                                                    label: 'Webpay',         icon: 'mdi-credit-card-outline',           color: '#2ea44f', bg: 'rgba(46,164,79,0.12)' },
        { test: /^\/sofono/,                                                    label: 'Sofonos',        icon: 'mdi-phone-forward',                 color: '#d04663', bg: 'rgba(208,70,99,0.12)' },
        { test: /^\/cotizaciones/,                                              label: 'Cotizaciones',   icon: 'mdi-file-document-edit-outline',    color: '#7b5af0', bg: 'rgba(123,90,240,0.12)' },
        { test: /^\/facturacion/,                                               label: 'Facturación',    icon: 'mdi-finance',                       color: '#2ea44f', bg: 'rgba(46,164,79,0.12)' },
        { test: /^\/(clients|users|roles|coupons|planes-servicios|mantenedor|categorias)/,  label: 'Mantenedores',   icon: 'mdi-cog-outline',                   color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
        { test: /^\/resumen-cliente/,                                             label: 'Resumen Cliente',icon: 'mdi-account-details-outline',       color: '#2575c7', bg: 'rgba(37,117,199,0.12)' },
        { test: /^\/(acceso-ip|smtp-|redes)/,                                   label: 'Redes',          icon: 'mdi-lan',                           color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
        { test: /^\/incidencias-masivas/,                                       label: 'Incidencias Masivas', icon: 'mdi-alert-circle-outline',     color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
        { test: /^\//,                                                          label: 'Dashboard',      icon: 'mdi-view-dashboard-outline',        color: '#5b6cff', bg: 'rgba(91,108,255,0.12)' },
      ];
      return modules.find(m => m.test.test(path)) || null;
    },

    currentModuleStyle() {
      const m = this.currentModule;
      if (!m) return {};
      return { color: m.color, background: m.bg, borderColor: 'transparent' };
    },

    layoutAccentStyle() {
      const m = this.currentModule;
      if (!m) return {};
      return { '--acc': m.color, '--acc-bg': m.bg };
    },

    currentBreadcrumb() {
      if (!this.$route) return '';
      const name = this.$route.name;
      const map = {
        'all-tickets': 'Todos los tickets',
        'ticket-detail': 'Detalle',
        'apps-todo-board': 'Kanban',
        'dashboard': 'Inicio',
        'mantenedor-templates': 'Templates de Correo',
        'mantenedor-template-detail': 'Templates de Correo',
        'categorias': 'Categorías',
        'resumen-cliente': 'Resumen Cliente',
        'incidencias-masivas': 'Todas las Incidencias',
        'incidencia-masiva-detalle': 'Detalle',
      };
      return map[name] || '';
    },

    currentBreadcrumbExtra() {
      if (!this.$route) return null;
      if (this.$route.name === 'ticket-detail' && this.$route.params.id) {
        return `#${this.$route.params.id}`;
      }
      if (this.$route.name === 'mantenedor-template-detail' && this.$route.params.templateKey) {
        return decodeURIComponent(this.$route.params.templateKey);
      }
      if (this.$route.name === 'incidencia-masiva-detalle' && this.$route.params.id) {
        return `#${this.$route.params.id}`;
      }
      return null;
    },

    bottomNavItems() {
      if (!this.isAuthenticated) return [];

      // Definición completa de los botones estáticos de Mobile
      const allMobileItems = [
        { icon: 'mdi-home-outline', name: 'dashboard.view', text: 'Inicio', link: '/' },
        { icon: 'mdi-file-document-outline', name: 'notas-cobro.view', text: 'Emitidas', link: '/notas-cobros' },
        { icon: 'mdi-ticket-outline', name: 'tickets.view', text: 'Tickets', link: '/apps/board' },
        { icon: 'mdi-check-circle-outline', name: 'habilitation.view', text: 'Servicios', link: '/apps/servicios/padres' }
      ];

      // Solo se exponen los ítems donde el usuario tiene el permiso real,
      // igual que en verifyPermissions (sin permisos temporales).
      const userPermissions = new Set(
        (this.isAuthenticated.user.permisos || []).map((p) => p.name),
      );

      // Retorna solo los ítems que el usuario tiene derecho a ver
      return allMobileItems.filter(item => userPermissions.has(item.name));
    }
  },
  // --- 3. CICLO DE VIDA Y OBSERVADORES ---
  watch: {
    google2fa_enable() {
      if (process.env.VUE_APP_DISABLE_2FA === 'true') return;
      if (this.isAuthenticated.access_token && !this.google2fa_enable) {
        this.viewAlertAuth();
      }
    },

    _ctxClient(newVal) {
      const path = this.$route && this.$route.path;
      if (!path) return;
      const CTX_ROUTES = [
        { test: p => p.startsWith('/apps/servicios'),         query: c => ({ search: c.rut, searchType: 'cliente_rut' }) },
        { test: p => p.startsWith('/notas-cobros'),           query: c => ({ search_by: 'rut', search: c.rut }) },
        { test: p => p.startsWith('/apps/board'),             query: c => ({ cliente: c.rut }) },
        { test: p => p.startsWith('/sofono'),                 query: c => ({ cliente: c.rut }) },
        { test: p => p.startsWith('/cotizaciones'),           query: c => ({ cliente: c.rut }) },
        { test: p => p.startsWith('/dashboardClient_search'), query: c => ({ search: c.rut }) },
        { test: p => p.startsWith('/resumen-cliente'),        query: c => ({ rut: c.rut }) },
      ];
      const match = CTX_ROUTES.find(r => r.test(path));
      if (!match) return;
      const targetQuery = newVal ? match.query(newVal) : {};
      this.$router.push({ path, query: targetQuery }).catch(() => {});
    },
  },
  created() {
    // Timestamp del último Esc (no reactivo: sólo lo usa el handler de teclado).
    this._escLastAt = 0;
    this.auth_();
  },
  mounted() {
    setTimeout(() => {
      if (process.env.VUE_APP_DISABLE_2FA === 'true') return;
      if (this.isAuthenticated.access_token && !this.google2fa_enable) {
        this.viewAlertAuth();
      }
    }, 1200);
    window.addEventListener('keydown', this._onGlobalKeydown);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this._onGlobalKeydown);
  },

  // --- 4. MÉTODOS ---
  methods: {
    ...mapActions(["auth"]),
    ...mapActions("app", ["setGlobalTheme", "showToast"]),
    ...mapActions("clientContext", { clearClientContext: "clearClient" }),
    ...mapMutations(["clerarAuthenticated"]),
    
    async auth_() {
      await this.auth();
    },

    togglePin() {
      this.isPinned = !this.isPinned;
      localStorage.setItem('isMenuPinned', this.isPinned);
      if (this.isPinned) {
        this.drawer = true;
      }
    },
    
    onKeyup(e) {
      if (this.$refs.search) this.$refs.search.focus();
    },

    _onGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.globalSearchOpen = true;
        return;
      }
      if (e.key === 'Escape') this._onEscapeKey(e);
    },

    // Doble Esc (dos pulsaciones seguidas) = salir del contexto de cliente.
    _onEscapeKey(e) {
      if (!this.hasClientContext) return;
      // El command palette y los diálogos/overlays manejan Esc por su cuenta.
      if (this.globalSearchOpen) return;
      if (document.querySelector('.v-dialog--active, .v-overlay--active, .v-menu__content.menuable__content__active')) return;
      // No interferir mientras se escribe: ahí Esc suele significar "limpiar campo".
      if (this._isTypingTarget(e.target)) return;

      const now = Date.now();
      if (now - this._escLastAt <= ESC_DOUBLE_WINDOW_MS) {
        this._escLastAt = 0;
        const name = this.clientContextName;
        this.clearClientContext();
        this.showToast(name ? `Saliste del contexto de ${name}` : 'Saliste del contexto de cliente');
      } else {
        this._escLastAt = now;
      }
    },

    _isTypingTarget(el) {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
    },

    openGlobalSearch() {
      this.globalSearchOpen = true;
    },

    onSearchOpenTicket(ticket) {
      this.$router.push({ name: 'ticket-detail', params: { id: ticket.id } }).catch(() => {});
    },

    onSearchAction(action) {
      if (action === 'create-ticket') {
        this.$router.push({ path: '/apps/board', query: { actionPage: 'crearticket' } }).catch(() => {});
      } else if (action === 'toggle-theme') {
        this.setGlobalTheme(!this.globalTheme);
      }
    },
    
    viewAlertAuth() {
      this.twoFaNotification.status = true;
    }
  }
};
</script>

<style scoped>
/*
|========================================================================
| ESTILOS LOCALES DEL COMPONENTE (SCOPED)
|========================================================================
*/
.buy-button {
  box-shadow: 1px 1px 18px #ee44aa;
}

.v-container-fluid {
  padding: 19px !important;
  padding-bottom: 0px !important;
  margin-bottom: 0px !important;
}

@media (max-width: 900px) {
  .responsive-v {
    padding: 5px !important;
    padding-top: 20px !important;
  }
}

/* Minimalist CSS Hamburger */
.minimalist-hamburger {
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 50%;
  transition: background-color 0.2s;
  color: inherit;
}
.minimalist-hamburger:hover {
  background-color: rgba(128, 128, 128, 0.1);
}

.hamburger-inner {
  width: 18px;
  height: 2px;
  background-color: currentColor;
  position: relative;
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), background-color 0.3s ease;
}

.hamburger-inner::before,
.hamburger-inner::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  background-color: currentColor;
  left: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
}

.hamburger-inner::before {
  top: -6px;
}

.hamburger-inner::after {
  top: 6px;
}

.minimalist-hamburger.is-active .hamburger-inner {
  background-color: transparent;
}

.minimalist-hamburger.is-active .hamburger-inner::before {
  transform: translateY(6px) rotate(45deg);
}

.minimalist-hamburger.is-active .hamburger-inner::after {
  transform: translateY(-6px) rotate(-45deg);
}

/* ─── Sidebar mockup styles ─── */
.sb-drawer .v-navigation-drawer__prepend { border-bottom: none !important; }

.sb-head {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border, rgba(255,255,255,0.08));
}
.sb-logo img { height: 26px; max-width: 70%; object-fit: contain; }
.sb-pin {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 7px; cursor: pointer;
  border: 0; background: transparent;
  transition: background 120ms, color 120ms;
  flex-shrink: 0;
}
.theme--dark .sb-pin, .theme-dark .sb-pin { color: #6d7585; }
.theme--light .sb-pin, .theme-light .sb-pin { color: #8a90a0; }
.sb-pin:hover { background: var(--hover, rgba(255,255,255,0.06)); }
.sb-pin.active { background: rgba(0,150,199,0.14); color: #4fbde0; }
.theme--light .sb-pin.active { background: rgba(0,150,199,0.10); color: #0096c7; }

.sb-search {
  margin: 10px 12px 6px;
  display: flex; align-items: center; gap: 8px; height: 32px;
  padding: 0 10px;
  border-radius: 8px; cursor: pointer;
  transition: border-color 120ms, background 120ms;
}
.theme--dark .sb-search, .theme-dark .sb-search {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: #6d7585;
}
.theme--dark .sb-search:hover, .theme-dark .sb-search:hover {
  border-color: rgba(255,255,255,0.14);
  color: #a4abb7;
}
.theme--light .sb-search, .theme-light .sb-search {
  background: rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.08);
  color: #8a90a0;
}
.theme--light .sb-search:hover, .theme-light .sb-search:hover {
  border-color: rgba(0,0,0,0.14);
  color: #5b6370;
}
.sb-search .mdi { font-size: 15px; }
.sb-search .text { font-size: 12.5px; flex: 1; }
.sb-search .kbd {
  font-size: 10px; padding: 1px 5px; border-radius: 4px;
  font-family: inherit; border: 1px solid;
}
.theme--dark .sb-search .kbd, .theme-dark .sb-search .kbd {
  background: var(--surface, #0A111A); border-color: rgba(255,255,255,0.12);
}
.theme--light .sb-search .kbd, .theme-light .sb-search .kbd {
  background: rgba(0,0,0,0.06); border-color: rgba(0,0,0,0.22); color: #5b6370;
}

.sb-foot {
  padding: 10px 14px;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px;
  font-size: 11.5px;
}
.theme--dark .sb-foot, .theme-dark .sb-foot { color: #6d7585; border-top: 1px solid rgba(255,255,255,0.06); }
.theme--light .sb-foot, .theme-light .sb-foot { color: #8a90a0; border-top: 1px solid rgba(0,0,0,0.06); }
.sb-foot-link {
  color: inherit;
  text-decoration: none;
  font-size: 11px;
  transition: color 100ms;
}
.sb-foot-link:hover { color: var(--v-primary-base, #0096c7); }

/* ─── Header content layout ─── */
.custom-glass-header .header-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
}

/* Module chip */
.module-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px 3px 7px; height: 24px;
  border-radius: 999px;
  font-size: 11.5px; font-weight: 600; letter-spacing: 0.01em;
  flex-shrink: 0;
}
.module-chip .mdi { font-size: 13px; }

/* Breadcrumbs */
.hdr-crumbs { display: flex; align-items: center; gap: 3px; }
.hdr-crumb-sep { font-size: 15px; opacity: 0.35; }
.theme--dark .hdr-crumb-sep { color: #a4abb7; }
.theme--light .hdr-crumb-sep { color: #5b6370; }
.hdr-crumb-here { font-size: 13px; font-weight: 500; }
.theme--dark .hdr-crumb-here { color: #e8eaef; }
.theme--light .hdr-crumb-here { color: #1b1d22; }
.hdr-crumb-mid { font-size: 13px; font-weight: 400; }
.theme--dark .hdr-crumb-mid { color: #6d7585; }
.theme--light .hdr-crumb-mid { color: #6b7280; }

/* Header search (right side) */
.hdr-search {
  display: inline-flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 10px;
  border-radius: 9px; cursor: pointer;
  font-family: inherit; font-size: 13px;
  transition: border-color 120ms;
  flex-shrink: 0;
}
.hdr-search--dark {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: #6d7585;
}
.hdr-search--dark:hover { border-color: rgba(255,255,255,0.16); color: #a4abb7; }
.hdr-search--light {
  background: rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.08);
  color: #8a90a0;
}
.hdr-search--light:hover { border-color: rgba(0,0,0,0.16); color: #5b6370; }
.hdr-search-text { flex: 1; white-space: nowrap; max-width: 180px; }
.hdr-search-kbd {
  font-size: 10px; padding: 1px 5px; border-radius: 4px;
  font-family: inherit; border: 1px solid; flex-shrink: 0;
}
.hdr-search--dark .hdr-search-kbd { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.12); }
.hdr-search--light .hdr-search-kbd { background: rgba(0,0,0,0.06); border-color: rgba(0,0,0,0.22); color: #5b6370; }

/* Mobile-only icon trigger for the global search */
.hdr-search-icon {
  align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px;
  background: transparent; cursor: pointer; flex-shrink: 0;
  transition: background 100ms, color 100ms;
}
.hdr-search-icon .mdi { font-size: 20px; }
.hdr-search-icon--dark { color: #a4abb7; border: 1px solid transparent; }
.hdr-search-icon--dark:hover { background: rgba(255,255,255,0.06); color: #e8eaef; }
.hdr-search-icon--light { color: #5b6370; border: 1px solid transparent; }
.hdr-search-icon--light:hover { background: rgba(0,0,0,0.05); color: #1a1a1a; }

/* Header divider */
.hdr-divider {
  width: 1px; height: 20px; flex-shrink: 0; align-self: center;
}
.theme--dark .hdr-divider { background: rgba(255,255,255,0.09); }
.theme--light .hdr-divider { background: rgba(0,0,0,0.09); }

/* --- Apple-like Pin Toggle (legacy, kept for safety) --- */
.apple-pin-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px; /* Slightly squarish like iOS buttons */
  background: transparent;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.apple-pin-toggle::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(142, 142, 147, 0.12); /* Subtle gray background */
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: inherit;
}

.apple-pin-toggle:hover::before {
  opacity: 1; /* Appears on hover */
}

.apple-pin-toggle:active {
  transform: scale(0.92);
}

.pin-icon-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pin-icon {
  color: #8e8e93 !important; /* Muted Apple Gray */
  transition: color 0.2s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Active State (Pinned) */
.apple-pin-toggle.is-pinned::before {
  background: var(--v-primary-base, #1976d2);
  opacity: 0.1;
}

.apple-pin-toggle.is-pinned .pin-icon {
  color: var(--v-primary-base, #1976d2) !important; /* Theme Primary Color */
  transform: rotate(45deg); /* Slight playful rotation when pinned */
}

/* Dark mode adjustment for pin toggle */
.theme--dark .apple-pin-toggle::before {
  background: rgba(255, 255, 255, 0.08);
}
.theme--dark .pin-icon {
  color: #98989d !important;
}
.theme--dark .apple-pin-toggle.is-pinned::before {
  background: var(--v-primary-base, #1976d2);
  opacity: 0.15;
}
.theme--dark .apple-pin-toggle.is-pinned .pin-icon {
  color: var(--v-primary-base, #1976d2) !important;
}

</style>

<style>
/*
|========================================================================
| ESTILOS GLOBALES DE DISEÑO (GLASSMORPHISM Y TEMA)
|========================================================================
*/

/* 
  1. Panel de Cristal Unificado

  Aplica un desenfoque de fondo real y un color de superficie altamente translúcido.
*/
.apple-glass-pane {
  background-clip: padding-box !important;
  backdrop-filter: blur(16px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(16px) saturate(180%) !important;
  /* Anti-aliasing para preservar texto nítido sobre el blur */
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
  box-shadow: none !important; /* Forza la eliminación de las sombras modales en drawers temporales */
}

/* Header background */
.apple-glass-header-light {
  background-color: rgba(255, 255, 255, 0.75) !important;
}
.apple-glass-header-dark {
  background-color: rgba(13, 17, 23, 0.88) !important;
}

/* Drawer background */
.apple-glass-drawer-light {
  background-color: rgba(255, 255, 255, 0.94) !important;
}
.apple-glass-drawer-dark {
  background-color: rgba(13, 17, 23, 0.97) !important;
}

/* Bordes de Cristal Simulados */
.apple-glass-pane.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.apple-glass-drawer-light.border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.apple-glass-pane.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.apple-glass-pane-light.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

      /* ------------------------------------
         2. Cabecera Fija Personalizada en CSS Puro
         ------------------------------------ */
      .custom-glass-header {
        position: fixed;
        top: 0;
        width: 100%;
        height: 55px;
        z-index: 10;
        display: flex;
        align-items: center;
        transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      /* Ajuste de ancho de cabecera cuando el menú está fijado en escritorio */
      @media (min-width: 1360px) {
        .custom-glass-header.header-shifted {
          width: calc(100% - 256px); /* 256px es el ancho por defecto de v-navigation-drawer */
        }
      }

/* header-content — defined in scoped block above */

/* ------------------------------------
   3. Navegación Inferior (Móvil)
   ------------------------------------ */
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(65px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 5;
  justify-content: space-around;
  align-items: center;
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: inherit;
  flex: 1;
  height: 100%;
  opacity: 0.6;
  transition: opacity 0.2s, transform 0.2s;
}

.bottom-nav-item .v-icon {
  font-size: 24px;
  margin-bottom: 2px;
  color: inherit !important;
}

.bottom-nav-item span {
  font-size: 10px;
  font-weight: 500;
}

.bottom-nav-item.active,
.bottom-nav-item:active {
  opacity: 1;
  transform: scale(1.05);
}

/* Light Mode: active is dark grey instead of blue */
.theme--light .bottom-nav-item {
  color: #1d1d1f !important;
}
.boxColorStyle_light .bottom-nav-item.active .v-icon {
  color: #1d1d1f !important;
}

/* Dark Mode: active is pure white instead of blue */
.boxColorStyle_dark .bottom-nav-item {
  color: #ffffff !important;
}
.boxColorStyle_dark .bottom-nav-item.active .v-icon {
  color: #ffffff !important;
}

/* Ajustes especiales para ToolbarUser dentro del Bottom Nav */
.bottom-nav-user {
  cursor: pointer;
}
.bottom-nav-user .v-btn {
  height: 24px !important;
  width: 24px !important;
  margin-bottom: 4px;
}
.bottom-nav-user .v-badge__badge {
  transform: scale(0.7) translate(2px, -2px);
}

/* ------------------------------------
   4. Fondo Ambiente Global
   ------------------------------------ */
.layout-root {
  position: relative;
  min-height: 100vh;
  background-color: var(--bg-base, #f4f5f8);
  overflow: hidden;
}

.global-ambient-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.ambient-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}

/* ── Light mode blobs (barely visible) ── */
.ambient-blob-1 {
  width: 55vw; height: 55vw;
  max-width: 680px; max-height: 680px;
  top: -15%; left: -8%;
  background: rgba(0, 135, 239, 0.06);
}
.ambient-blob-2 {
  width: 45vw; height: 45vw;
  max-width: 560px; max-height: 560px;
  bottom: 5%; right: -6%;
  background: rgba(2, 106, 175, 0.04);
}

/* ── Dark mode — sin blobs, fondo neutro limpio ── */
.theme--dark .ambient-blob-1,
.theme-dark .ambient-blob-1,
.theme--dark .ambient-blob-2,
.theme-dark .ambient-blob-2,
.theme--dark .ambient-blob-3,
.theme-dark .ambient-blob-3 {
  display: none;
}

/* Asegura que el v-main se posicione explícitamente sobre el fondo ambiente */
.v-main {
  position: relative;
  z-index: 1;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  /* padding-top: 55px !important; Compensación para the cabecera fixed */
}

@media (max-width: 599px) {
  .v-main {
    /* Espacio extra abajo en móviles para que no estorbe el mobile-bottom-nav */
    padding-bottom: calc(75px + env(safe-area-inset-bottom)) !important;
  }
}

/* ------------------------------------
   5. Limpieza Interna de Vuetify
   ------------------------------------ */
.apple-glass-pane .v-navigation-drawer__content,
.apple-glass-pane .v-toolbar__content,
.apple-glass-pane .v-toolbar__extension {
  background: transparent !important;
  background-image: none !important; /* Elimina los degradados/sombras molestas de fondo del modo Claro */
  box-shadow: none !important;
  position: relative;
  z-index: 2;
}

.apple-glass-pane .v-navigation-drawer__image,
.apple-glass-pane .v-toolbar__image {
  display: none !important;
}

.apple-glass-pane::before {
  display: none !important; 
}
</style>
